/* ============================================================
   Стор турниров MLBB (квалификации MCA League от QNEA).

   ИСТОЧНИК ПРАВДЫ — БЭКЕНД (/api/fed-teams): профили игроков, команды,
   заявки на турнир, счёт матчей и чек-ин «Готов» живут на сервере,
   поэтому коды приглашений и сетка работают между разными
   пользователями и устройствами.

   ОДИН стор на ВСЕ турниры MLBB: команды и профили общие для игры, а
   заявки/счёт/чек-ин — по активному турниру (tourId). Страница турнира
   объявляет свой id через setTour()/startPolling(id); при смене турнира
   привязанные к нему данные сбрасываются и подтягиваются заново.

   localStorage (zustand persist) — офлайн-кэш и фолбэк: если бэкенд
   недоступен (online: false), уже загруженные данные остаются видны
   и механика продолжает работать локально до восстановления связи.

   Никаких вшитых/демо-команд: в списках и сетке только реальные
   команды с сервера. Пустой турнир = пустая сетка.
   ============================================================ */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiFetch, useAppStore } from '../../store';

// id турниров = tour_id на бэке (см. fed/data.js). Первые квалы носят
// исторический id 'mlbb-women-2026' — менять его нельзя, иначе осиротеют
// уже поданные заявки и сыгранные матчи.
export const MLBB_TOUR_ID = 'mlbb-women-2026';        // квалификации #1
export const MLBB_TOUR_ID_Q2 = 'mlbb-mca-quals2-2026'; // квалификации #2
/** Турнир по умолчанию — тот, где сейчас идёт приём заявок */
export const MLBB_ACTIVE_TOUR_ID = MLBB_TOUR_ID_Q2;
/** Игра этого стора: команды/профили на бэке общие для всех турниров игры */
const DEFAULT_GAME = 'mlbb';

// Состав команды: 5 игроков основы (обязательны для заявки) + 2 слота замены
// по желанию. Всего до 7 человек. См. TEAM_MAIN/TEAM_MAX на бэке (fed_teams.py).
export const TEAM_MAIN = 5;
export const TEAM_MAX = 7;

// Окно чек-ина матча: 15 минут на то, чтобы обе команды отметились «Готов»
// после открытия матча админом. По истечении — техрезультат (см. бэкенд).
export const CHECKIN_WINDOW_SEC = 15 * 60;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5252/api';

// ===== API-хелпер: короткие коды ошибок ('not-found', 'full', …) пробрасываем
// в err.code, сетевые сбои — отдельно (err.offline), чтобы честно показать,
// что данные не были сохранены =====
const api = async (path, { method = 'GET', body } = {}) => {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem('access_token');
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (e) {}
  let res;
  try {
    res = await apiFetch(`${API_URL}/fed-teams${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    const err = new Error('offline');
    err.offline = true;
    throw err;
  }
  let data = {};
  try { data = await res.json(); } catch (e) {}
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.code = data.error;
    throw err;
  }
  return data;
};

// ===== чистые функции сетки =====

const scoreWinner = (a, b, res = {}) => {
  const sa = Number(res.sa);
  const sb = Number(res.sb);
  if (Number.isFinite(sa) && Number.isFinite(sb) && res.sa !== '' && res.sb !== '' && sa !== sb) {
    return sa > sb ? a : b;
  }
  return null;
};

/** Позиции стандартного посева для сетки размера n (степень двойки).
    seedPositions(n)[i] — номер посева (1..n) в слоте i. Классическая схема:
    1 играет с n, 2 c n-1 и т.д.; сильные посевы разведены по разным веткам,
    поэтому пустые слоты (BYE) не сваливаются в конец, а раскиданы по сетке. */
function seedPositions(n) {
  let order = [1];
  while (order.length < n) {
    const len = order.length;
    const next = [];
    for (const s of order) {
      next.push(s);
      next.push(2 * len - s + 1); // «зеркальный» посев для этого слота
    }
    order = next;
  }
  return order;
}

/** Сетка single elimination: из порядка команд + результатов ({matchId:{sa,sb}})
    выводим раунды. Победители протекают в следующий раунд автоматически.
    Команды расставляются по стандартному посеву (порядок teamIds = посев,
    первый подавший заявку — 1-й посев); недостающие до степени двойки слоты
    становятся BYE и достаются верхним посевам, распределяясь по сетке.
    BYE (авто-проход) — когда слот соперника гарантированно пуст (посев без
    пары или «мёртвая» ветка), а не пока матч соперников не доигран. */
export function buildRounds(teamIds, results = {}) {
  if (!teamIds.length) return [];
  const n = Math.max(2, 2 ** Math.ceil(Math.log2(Math.max(teamIds.length, 2))));
  const k = teamIds.length;
  // посев s (1..n) → команда teamIds[s-1], если она есть, иначе пустой слот (BYE)
  const slots = seedPositions(n).map((seed) => (seed <= k ? teamIds[seed - 1] : null));

  const rounds = [];
  let prev = null;
  for (let ri = 0, num = n / 2; num >= 1; ri += 1, num /= 2) {
    const matches = [];
    for (let mi = 0; mi < num; mi += 1) {
      let a, b, aDead, bDead;
      if (ri === 0) {
        a = slots[2 * mi];
        b = slots[2 * mi + 1];
        aDead = !a;
        bDead = !b;
      } else {
        const fa = prev[2 * mi];
        const fb = prev[2 * mi + 1];
        a = fa.winner;
        b = fb.winner;
        // «мёртвая» ветка (нет команд) ИЛИ двойное тех. — дальше никто не идёт
        aDead = fa.dead || fa.voided;
        bDead = fb.dead || fb.voided;
      }
      const id = `r${ri}m${mi}`;
      const res = results[id] || {};
      const dead = aDead && bDead; // ветка без команд — матча не будет
      const bye = !dead && ((!!a && bDead) || (!!b && aDead));

      // Исход матча. BYE → единственная команда. Иначе явное решение важнее
      // счёта: double_ff (обе не зашли) обнуляет ветку, winner (тех.победа/
      // подтверждение админа) двигает победителя, счёт — фолбэк.
      let winner = null;
      let voided = false;
      if (bye) {
        winner = a || b;
      } else if (a && b) {
        if (res.status === 'double_ff') voided = true;
        else if (res.winner && (res.winner === a || res.winner === b)) winner = res.winner;
        else winner = scoreWinner(a, b, res);
      }

      matches.push({
        id, ri, mi, a, b,
        sa: res.sa ?? '', sb: res.sb ?? '',
        bye, dead, voided, winner,
        status: res.status || '',
        opened: !!res.opened,
        remaining: res.remaining ?? null,
        proof: res.proof || null,
      });
    }
    rounds.push(matches);
    if (num === 1) break;
    prev = matches;
  }
  return rounds;
}

export const ROUND_TITLES = {
  ru: { 8: '1/8 финала', 4: '1/4 финала', 2: 'Полуфиналы', 1: 'Финал' },
  kz: { 8: '1/8 финал', 4: '1/4 финал', 2: 'Жартылай финал', 1: 'Финал' },
};

export const useMlbb = create(
  persist(
    (set, get) => ({
      /** Профили игроков MLBB: { [userKey]: { gameId, nick, tg } } */
      profiles: {},
      /** Команды (с сервера) */
      teams: [],
      /** Заявки: [{ teamId, tourId }] */
      regs: [],
      /** Результаты матчей: { [matchId]: { sa, sb } } */
      results: {},
      /** Чек-ин: { [`${matchId}|${teamId}`]: [memberKey, …] } */
      ready: {},
      /** Бэкенд доступен: true — сервер источник правды, false — локальный демо-режим */
      online: false,
      /** Активный турнир: к нему привязаны заявки, счёт и чек-ин */
      tourId: MLBB_ACTIVE_TOUR_ID,
      game: DEFAULT_GAME,

      // ===== синхронизация с бэкендом =====

      /** Переключить активный турнир. Команды и профили общие для игры и
          остаются, а привязанные к турниру данные сбрасываем — иначе на
          новом турнире мелькнут заявки и результаты предыдущего. */
      setTour: (tourId, game = DEFAULT_GAME) => {
        if (!tourId) return;
        const gameChanged = game !== get().game;
        if (tourId === get().tourId && !gameChanged) return;
        set({
          tourId,
          game,
          regs: [], results: {}, ready: {},
          ...(gameChanged ? { teams: [], profiles: {} } : {}),
        });
      },

      /** Применить состояние с сервера (форма ответа совпадает со стором) */
      applyState: (data) => {
        if (!data || !Array.isArray(data.teams)) return;
        const patch = {
          teams: data.teams,
          regs: data.regs || [],
          results: data.results || {},
          ready: data.ready || {},
          online: true,
        };
        if (data.userKey) {
          patch.profiles = { ...get().profiles };
          if (data.profile) patch.profiles[data.userKey] = data.profile;
          else delete patch.profiles[data.userKey];
        }
        set(patch);
      },

      /** Подтянуть состояние турнира с сервера. При сбое оставляем ранее
          загруженные данные только для чтения. */
      hydrate: async () => {
        const { tourId, game } = get();
        const account = useAppStore.getState().user?.id;
        try {
          const data = await api(`/state?tour=${tourId}&game=${game}`);
          // A response from the previous game must not populate the new tournament.
          if (get().tourId === tourId && get().game === game && useAppStore.getState().user?.id === account) get().applyState(data);
        } catch (e) {
          if (e.offline && get().tourId === tourId && get().game === game) set({ online: false });
        }
      },

      /** Поллинг состояния (чек-ин соперников, счёт) пока открыта страница
          турнира. tourId — турнир этой страницы (переключает активный).
          Возвращает функцию остановки — для cleanup в useEffect. */
      startPolling: (tourId, game = DEFAULT_GAME) => {
        get().setTour(tourId, game);
        const tick = () => get().hydrate();
        tick();
        const timer = setInterval(tick, 20000);
        window.addEventListener('focus', tick);
        return () => {
          clearInterval(timer);
          window.removeEventListener('focus', tick);
        };
      },

      /** Серверная мутация. Регистрация, профиль и состав не имеют локального
          «успешного» режима: пользователь видит успех только после ответа API.
          Иначе при обрыве сети заявка оставалась лишь в localStorage. */
      _mutate: async (_localPatch, request) => {
        if (!request) return 'offline';
        const { tourId, game } = get();
        const account = useAppStore.getState().user?.id;
        try {
          const data = await request();
          if (get().tourId === tourId && get().game === game && useAppStore.getState().user?.id === account) get().applyState(data);
          window.dispatchEvent(new Event('participant-notices:refresh'));
          return null;
        } catch (e) {
          if (e.offline) { set({ online: false }); return 'offline'; }
          get().hydrate();
          return e.code || 'server-error';
        }
      },

      // ===== профиль игрока =====
      saveProfile: (userKey, patch) =>
        get()._mutate(
          (s) => {
            const profile = { gameId: '', nick: '', tg: '', ...s.profiles[userKey], ...patch };
            return {
              profiles: { ...s.profiles, [userKey]: profile },
              // данные участника обновляются и в составах его команд
              teams: s.teams.map((t) =>
                t.members.some((m) => m.key === userKey)
                  ? { ...t, members: t.members.map((m) => (m.key === userKey ? { ...m, ...profile } : m)) }
                  : t
              ),
            };
          },
          () => api('/profile', { method: 'POST', body: { ...patch, game: get().game, tour: get().tourId } })
        ),

      // ===== команды =====

      /** Создать команду. Возвращает ошибку строкой или null. */
      createTeam: async (userKey, { name, tag }) => {
        if (!get().profiles[userKey]) return 'no-profile';
        try {
          get().applyState(await api('/teams', { method: 'POST', body: { name, tag, game: get().game, tour: get().tourId } }));
          return null;
        } catch (e) {
          if (e.offline) { set({ online: false }); return 'offline'; }
          return e.code || 'server-error';
        }
      },

      /** Вступить по коду приглашения. Возвращает ошибку строкой или null. */
      joinTeam: async (userKey, rawCode) => {
        const c = (rawCode || '').trim().toUpperCase();
        if (!get().profiles[userKey]) return 'no-profile';
        try {
          get().applyState(await api('/teams/join', { method: 'POST', body: { code: c, tour: get().tourId } }));
          return null;
        } catch (e) {
          if (e.offline) { set({ online: false }); return 'offline'; }
          return e.code || 'server-error';
        }
      },

      leaveTeam: (userKey, teamId) =>
        get()._mutate(
          (s) => ({
            teams: s.teams.map((t) =>
              t.id === teamId ? { ...t, members: t.members.filter((m) => m.key !== userKey) } : t
            ),
          }),
          () => api(`/teams/${teamId}/leave`, { method: 'POST', body: { tour: get().tourId } })
        ),

      removeMember: (teamId, memberKey) =>
        get()._mutate(
          (s) => ({
            teams: s.teams.map((t) =>
              t.id === teamId ? { ...t, members: t.members.filter((m) => m.key !== memberKey) } : t
            ),
          }),
          () => api(`/teams/${teamId}/members/${encodeURIComponent(memberKey)}?tour=${get().tourId}`, { method: 'DELETE' })
        ),

      deleteTeam: (teamId) =>
        get()._mutate(
          (s) => ({
            teams: s.teams.filter((t) => t.id !== teamId),
            regs: s.regs.filter((r) => r.teamId !== teamId),
          }),
          () => api(`/teams/${teamId}?tour=${get().tourId}`, { method: 'DELETE' })
        ),

      /** Демо: дозаполнить состав тестовыми игроками */
      fillTeamDemo: (teamId) =>
        get()._mutate(
          (s) => ({
            teams: s.teams.map((t) => {
              if (t.id !== teamId) return t;
              const members = [...t.members];
              let i = members.length;
              while (members.length < TEAM_MAIN) {
                members.push({
                  key: `${teamId}-demo-${i}`,
                  nick: `Player_${teamId}${i + 1}`,
                  gameId: String(60_000_000 + (teamId.charCodeAt(0) || 65) * 10_000 + i * 111),
                  tg: `@player_${teamId.toLowerCase()}${i + 1}`,
                  demo: true,
                });
                i += 1;
              }
              return { ...t, members };
            }),
          }),
          () => api(`/teams/${teamId}/fill-demo`, { method: 'POST', body: { tour: get().tourId } })
        ),

      // ===== заявки на турнир =====
      // tour не передан — заявка на активный турнир страницы (get().tourId)
      registerTeam: (teamId, tour) => {
        const tourId = tour || get().tourId;
        return get()._mutate(
          (s) =>
            s.regs.some((r) => r.teamId === teamId && r.tourId === tourId)
              ? s
              : { regs: [...s.regs, { teamId, tourId }] },
          () => api(`/teams/${teamId}/register`, { method: 'POST', body: { tour: tourId } })
        );
      },

      unregisterTeam: (teamId, tour) => {
        const tourId = tour || get().tourId;
        return get()._mutate(
          (s) => ({ regs: s.regs.filter((r) => !(r.teamId === teamId && r.tourId === tourId)) }),
          () => api(`/teams/${teamId}/unregister`, { method: 'POST', body: { tour: tourId } })
        );
      },

      // ===== матчи =====
      setResult: (matchId, sa, sb) =>
        get()._mutate(
          (s) => ({ results: { ...s.results, [matchId]: { ...s.results[matchId], sa, sb } } }),
          () => api(`/results/${matchId}`, { method: 'PUT', body: { tour: get().tourId, game: get().game, sa, sb } })
        ),

      /** Админ открывает матч → старт 15-минутного окна чек-ина. a/b — коды
          пары (нужны серверу для авто-техрезультата при неявке). */
      openMatch: (matchId, a, b) =>
        get()._mutate(
          (s) => {
            const cur = s.results[matchId] || {};
            const keepConfirmed = cur.status === 'confirmed';
            return {
              results: {
                ...s.results,
                [matchId]: {
                  ...cur,
                  opened: true,
                  remaining: CHECKIN_WINDOW_SEC,
                  status: keepConfirmed ? 'confirmed' : '',
                  winner: keepConfirmed ? cur.winner : null,
                },
              },
            };
          },
          () => api(`/match/${matchId}/open`, { method: 'POST', body: { tour: get().tourId, game: get().game, a, b } })
        ),

      /** Админ фиксирует исход матча.
          status: 'confirmed'|'walkover' (нужен winner) | 'double_ff' | '' (полный сброс). */
      resolveMatch: (matchId, winner, status) =>
        get()._mutate(
          (s) => {
            const cur = s.results[matchId] || {};
            // '' — полный сброс матча в исходное (совпадает с бэкендом)
            const next = status === ''
              ? { sa: '', sb: '', status: '', winner: null, opened: false, remaining: null, proof: null }
              : { ...cur, status, winner: status === 'double_ff' ? null : winner || null };
            return { results: { ...s.results, [matchId]: next } };
          },
          () => api(`/match/${matchId}/resolve`, { method: 'POST', body: { tour: get().tourId, game: get().game, winner: winner || '', status } })
        ),

      /** Участник/капитан грузит скриншот-подтверждение (multipart).
          Возвращает код ошибки строкой или null при успехе. */
      submitProof: async (matchId, teamCode, file) => {
        if (!get().online) return 'offline';
        const headers = {};
        try {
          const token = localStorage.getItem('access_token');
          if (token) headers.Authorization = `Bearer ${token}`;
        } catch (e) {}
        const fd = new FormData();
        fd.append('screenshot', file);
        fd.append('teamId', teamCode);
        let res;
        try {
          res = await apiFetch(`${API_URL}/fed-teams/match/${matchId}/proof?tour=${get().tourId}&game=${get().game}`, {
            method: 'POST',
            headers,
            body: fd,
          });
        } catch (e) {
          return 'offline';
        }
        let data = {};
        try { data = await res.json(); } catch (e) {}
        if (!res.ok) return data.error || 'server-error';
        get().applyState(data);
        return null;
      },

      toggleReady: (matchId, teamId, memberKey) =>
        get()._mutate(
          (s) => {
            const k = `${matchId}|${teamId}`;
            const cur = s.ready[k] || [];
            const next = cur.includes(memberKey) ? cur.filter((m) => m !== memberKey) : [...cur, memberKey];
            return { ready: { ...s.ready, [k]: next } };
          },
          () => api('/ready/toggle', { method: 'POST', body: { tour: get().tourId, game: get().game, matchId, teamId, memberKey } })
        ),

      readyAll: (matchId, teamId, memberKeys) =>
        get()._mutate(
          (s) => ({ ready: { ...s.ready, [`${matchId}|${teamId}`]: [...memberKeys] } }),
          () => api('/ready/all', { method: 'POST', body: { tour: get().tourId, game: get().game, matchId, teamId } })
        ),

      /** Полный сброс турнира (админ): успех только после ответа сервера. */
      resetMlbb: () =>
        get()._mutate(
          { teams: [], regs: [], results: {}, ready: {} },
          () => api('/reset', { method: 'POST', body: { tour: get().tourId, game: get().game } })
        ),
    }),
    {
      name: 'fed-mlbb-v1',
      version: 4,
      // online — состояние сессии, в кэш не пишем. tourId сохраняем: он
      // говорит, какому турниру принадлежат закэшированные заявки/счёт.
      partialize: ({ tourId, game }) => ({ tourId, game }),
      migrate: (persisted, version) => {
        if (persisted && typeof persisted === 'object') {
          // v2: демо-команды убраны — чистим закэшированный мок из старых браузеров
          // v3: появились вторые квалы — в старом кэше нет tourId, а заявки и
          //     счёт лежат от первых квал; чистим, чтобы не смешались с новым
          if (version < 3) {
            delete persisted.teams;
            delete persisted.regs;
            delete persisted.results;
            delete persisted.ready;
          }
        }
        // Remove old public snapshots containing private IDs/contacts as well.
        return { tourId: persisted?.tourId || MLBB_ACTIVE_TOUR_ID, game: persisted?.game || DEFAULT_GAME };
      },
    }
  )
);

useAppStore.subscribe((state, previous) => {
  if (state.user?.id !== previous.user?.id) {
    useMlbb.setState({ teams: [], profiles: {}, regs: [], results: {}, ready: {}, online: false });
    useMlbb.getState().hydrate();
  }
});

// ===== селекторы-помощники =====

export const userKeyOf = (user) => (user ? `u${user.id ?? user.nickname ?? user.email}` : null);

export const myTeamsOf = (state, userKey) =>
  userKey ? state.teams.filter((t) => t.members.some((m) => m.key === userKey)) : [];

export const regTeamIdsOf = (state, tourId = state.tourId) =>
  state.regs.filter((r) => r.tourId === tourId).map((r) => r.teamId);
